import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import {
  groupByCarrierAndCustom,
  groupByCustomAndAddGenericPackage,
} from '../../../../../fulfillment-planner/package/dropdown/package-group-resolver';
import { useGetInitialShippingServiceGroups } from '../../../../../hooks/use-get-initial-shipping-service-groups';
import useProviderOrCarrierGroups from '../../../../../components/shipping-service-selector/group-hooks/use-provider-or-carrier-groups';
import useServiceCollectionGroup from '../../../../../components/shipping-service-selector/group-hooks/use-service-collection-group';
import { ACCESS_LEVELS, useRequiresRateShopperAccessLevel } from '../../../../../hooks/use-has-rate-shopper-access-level';
import Global from '../../../../../store/global-selectors';
import { PackageService } from '../../../../../services/package-service';

const getGroupingFunction = (sellerCarriers, selectedService) => {
  if (!selectedService || !selectedService.carrierId) {
    return groupByCustomAndAddGenericPackage;
  }

  const carrier = sellerCarriers.find((c) => c.carrierId === selectedService.carrierId);
  const carriers = carrier ? [carrier] : [];
  return (packages) => groupByCarrierAndCustom(packages, carriers);
};

const makeSelectGroupedPackages = () =>
  createSelector(
    [
      (state, serviceId) => Global.selectSellerServiceById(state, { serviceId }),
      Global.selectSellerPackages,
      Global.selectReferenceDatagetSellerCarriers,
    ],
    (selectedService, packages, sellerCarriers) => {
      const carriers = sellerCarriers || [];
      const filterFunction = PackageService.getFilterFunction(selectedService);
      const groupingFunction = getGroupingFunction(carriers, selectedService);
      const filteredPackages = packages.filter((pkg) => filterFunction(pkg));

      return groupingFunction(filteredPackages);
    },
  );

/**
 * @typedef {object} ShippingServiceMapping
 * @property {string} name
 * @property {string} [serviceId]
 * @property {string} [sellerLabelProviderId]
 * @property {string} [serviceCollectionGuid]
 * @property {boolean} [isTemplate]
 */

/**
 * @typedef {object} ShippingServiceGroup
 * @property {string} name
 * @property {ShippingServiceMapping[]} items
 * @property {string} groupId
 * @property {string} [groupClassname]
 */

/**
 * @typedef {object} PackageGroup
 * @property {string | FormattedMessagePropType} groupName
 * @property {Array} packages
 * @property {string} [selectedPackageKey]
 * @property {string} [itemClassName]
 * @property {string} [groupClassname]
 */

/**
 * Custom hook that provides data for form elements in the service mapping modal
 * @param {object} props
 * @param {string} [props.serviceId] - The currently selected service ID
 * @param {string} [props.serviceCollectionGuid] - The currently selected service collection GUID
 * @returns {{
 *   serviceGroups: ShippingServiceGroup[],
 *   packageGroups: PackageGroup[],
 *   selectedServiceOption: ShippingServiceMapping
 * }}
 */
export const useServiceMappingDropdowns = ({ serviceId, serviceCollectionGuid }) => {
  const selectGroupedPackages = useMemo(() => makeSelectGroupedPackages(), []);
  const packageGroups = useSelector((state) => selectGroupedPackages(state, serviceId));
  const merchantServicesRequired = useSelector(Global.selectCarrierSharingisRequired);
  const serviceCollections = useSelector(Global.selectSellerServiceCollections);
  const selectedServiceCollection = serviceCollections.find((collection) => collection.serviceCollectionGuid === serviceCollectionGuid);
  const isTemplate = selectedServiceCollection?.isTemplate;
  const allServices = useSelector(Global.selectSellerServices);

  const selectedServiceItem = useMemo(
    () => ({
      serviceId: serviceCollectionGuid ? undefined : serviceId,
      serviceCollectionGuid,
    }),
    [serviceCollectionGuid, serviceId],
  );

  const canUseSearchableDropdown = true;
  const providerOrCarrierGroups = useProviderOrCarrierGroups(allServices, selectedServiceItem);
  const serviceCollectionGroup = useServiceCollectionGroup(selectedServiceItem, canUseSearchableDropdown);
  const showRateShopper = useRequiresRateShopperAccessLevel(ACCESS_LEVELS.FREEMIUM);
  const serviceGroups = useGetInitialShippingServiceGroups(providerOrCarrierGroups, merchantServicesRequired);

  const showRateShopperGroup = showRateShopper && serviceCollectionGroup !== null && !merchantServicesRequired;
  if (showRateShopperGroup) {
    serviceGroups.unshift(serviceCollectionGroup);
  }

  const selectedServiceOption = useMemo(() => {
    return {
      ...selectedServiceItem,
      isTemplate,
    };
  }, [isTemplate, selectedServiceItem]);

  return {
    serviceGroups,
    packageGroups,
    selectedServiceOption,
  };
};
